import React, { useState, useEffect } from 'react'
import {
 Plus,
 Search,
 Car,
 FileText,
 AlertCircle,
 CheckCircle,
 ArrowLeft
} from 'lucide-react'
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 AlertMessage,
 Toast,
 ToastType,
 ModernInput,
 ModernSelect,
 ModernButton,
 StatsCard,
 ListPageSkeleton,
} from '../../components/ui'
import { FinancialChargeForm, FinancialChargesListView } from '../../components/financial'
import { FinancialCharge, FinancialChargeFormData } from '../../types'
import { useFinancialCharges } from '../../hooks/useFinancialCharges'
import { useFinancialChargeOptions } from '../../hooks/useFinancialChargeOptions'

type View = 'list' | 'form'

export const FinancialChargesPage: React.FC = () => {
 const { charges, isLoading, error, pagination, fetchCharges, createCharge, updateCharge, deleteCharge } = useFinancialCharges()
 const { options } = useFinancialChargeOptions()

 const [view, setView] = useState<View>('list')
 const [searchTerm, setSearchTerm] = useState('')
 const [typeFilter, setTypeFilter] = useState<string>('all')
 const [selectedCharge, setSelectedCharge] = useState<FinancialCharge | null>(null)
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

 useEffect(() => {
 const params: any = { page: 1, limit: 50 }
 if (typeFilter && typeFilter !== 'all') params.type = typeFilter
 if (searchTerm) params.search = searchTerm
 const timer = setTimeout(() => fetchCharges(params), searchTerm ? 500 : 0)
 return () => clearTimeout(timer)
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchTerm, typeFilter])

 const handleOpenForm = (charge?: FinancialCharge) => {
 setSelectedCharge(charge || null)
 setView('form')
 }

 const handleBack = () => {
 setView('list')
 setSelectedCharge(null)
 }

 const handleSubmit = async (data: FinancialChargeFormData) => {
 setIsSubmitting(true)
 try {
 if (selectedCharge) {
 await updateCharge(selectedCharge.id, data)
 setToast({ message: 'Encargo atualizado com sucesso!', type: 'success' })
 } else {
 await createCharge(data)
 setToast({ message: 'Encargo criado com sucesso!', type: 'success' })
 }
 handleBack()
 } catch (err) {
 console.error('Erro ao salvar encargo:', err)
 setToast({ message: 'Erro ao salvar encargo. Tente novamente.', type: 'error' })
 } finally {
 setIsSubmitting(false)
 }
 }

 const handleDelete = async (id: string) => {
 if (confirm('Tem certeza que deseja excluir este encargo?')) {
 try {
 await deleteCharge(id)
 setToast({ message: 'Encargo excluído com sucesso!', type: 'success' })
 } catch (err) {
 console.error('Erro ao deletar encargo:', err)
 setToast({ message: 'Erro ao deletar encargo. Tente novamente.', type: 'error' })
 }
 }
 }

 const getTypeCount = (typeValue: string) => charges.filter(c => c.type === typeValue).length
 const getStatusCount = (isActive: boolean) => charges.filter(c => c.isActive === isActive).length

 const typeOptions = [{ value: 'all', label: 'Todos os tipos' }, ...options.types]

 // ── FORM VIEW ──────────────────────────────────────────────────────────────
 if (view === 'form') {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <ModernButton variant="outline"size="sm"onClick={handleBack}>
 <ArrowLeft className="h-4 w-4 mr-1"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">
 {selectedCharge ? 'Editar Encargo' : 'Novo Encargo'}
 </h1>
 <p className="text-sm text-muted-foreground mt-0.5">
 {selectedCharge ? `Editando: ${selectedCharge.name}` : 'Preencha os dados do novo encargo financeiro'}
 </p>
 </div>
 </div>

 <FinancialChargeForm
 charge={selectedCharge}
 onSubmit={handleSubmit}
 onCancel={handleBack}
 isSubmitting={isSubmitting}
 />

 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
 )}
 </div>
 )
 }

 // ── LIST VIEW ──────────────────────────────────────────────────────────────
 return (
 <div className="space-y-6">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">Encargos Financeiros</h1>
 <p className="mt-1 text-sm text-muted-foreground">
 Gerencie os encargos financeiros para propostas
 </p>
 </div>
 <div className="mt-4 sm:mt-0">
 <ModernButton onClick={() => handleOpenForm()}>
 <Plus className="h-4 w-4 mr-2"/>
 Novo Encargo
 </ModernButton>
 </div>
 </div>

 {error && <AlertMessage variant="error" message={error} />}
 {isLoading ? (
 <ListPageSkeleton
 variant="table"
 rows={6}
 showFilters={true}
 showStats={true}
 statsCount={4}
 />
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 <StatsCard title="Total de Encargos" value={charges.length} icon={FileText} iconColor="text-blue-600" />
 <StatsCard title="Encargos Ativos" value={getStatusCount(true)} icon={CheckCircle} iconColor="text-green-600" />
 <StatsCard title="Encargos Inativos" value={getStatusCount(false)} icon={AlertCircle} iconColor="text-muted-foreground" />
 <StatsCard title="Transferências" value={getTypeCount('transfer')} icon={Car} iconColor="text-purple-600" />
 </div>

 <Card>
 <CardContent className="p-6">
 <div className="flex flex-col sm:flex-row gap-4">
 <div className="flex-1">
 <ModernInput
 placeholder="Pesquisar encargos..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 leftIcon={<Search className="h-4 w-4" />}
 />
 </div>
 <div className="sm:w-48">
 <ModernSelect
 value={typeFilter}
 onChange={(e) => setTypeFilter(e.target.value)}
 options={typeOptions}
 placeholder="Filtrar por tipo"
 />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>Lista de Encargos ({pagination.total})</CardTitle>
 {pagination.totalPages > 1 && (
 <div className="text-sm text-muted-foreground">
 Página {pagination.page} de {pagination.totalPages}
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent>
 <FinancialChargesListView
 charges={charges}
 onEditCharge={handleOpenForm}
 onDeleteCharge={handleDelete}
 />

 {pagination.totalPages > 1 && (
 <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
 <div className="text-sm text-muted-foreground">
 Mostrando {charges.length} de {pagination.total} encargos
 </div>
 <div className="flex gap-2">
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => fetchCharges({ page: pagination.page - 1, limit: 50, ...(typeFilter !== 'all' && { type: typeFilter }), ...(searchTerm && { search: searchTerm }) })}
 disabled={pagination.page === 1}
 >
 Anterior
 </ModernButton>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => fetchCharges({ page: pagination.page + 1, limit: 50, ...(typeFilter !== 'all' && { type: typeFilter }), ...(searchTerm && { search: searchTerm }) })}
 disabled={pagination.page === pagination.totalPages}
 >
 Próxima
 </ModernButton>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 </>
 )}

 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
 )}
 </div>
 )
}
